import React from 'react';

import { AppState } from '../../types/game';
import { StatusBar, StatusBarProps } from '../statusbar';
import SelectedProductCompactView from '../SelectedProductCompactView';
import TitleBar from '../TitleBar';
import { useAppSelector } from '../../store/hooks';
import { selectIsProductConfirmationMode } from '../../store/selectors';
import { formatTime } from '../../utils/Formatting';
import { useSettingsRedux } from '../../hooks/useSettingsRedux';
import { DISCOUNT_AMOUNT } from '../../constants/gameConfig';
import parentFrameMessaging from '../../services/ParentFrameMessaging';
import './MainLayout.css';
import './animations.css';

export interface MainLayoutProps {
  statusBarProps: StatusBarProps;
  children: React.ReactNode;
  rootPath: string;
  location?: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({ statusBarProps, children, rootPath, location }) => {
  const isProductConfirmationMode = useAppSelector(selectIsProductConfirmationMode);
  const { selectedProduct, appState, setAppState, timeRemaining } = statusBarProps;

  // State for managing the transition to compact view
  const [isTransitioningToCompact, setIsTransitioningToCompact] = React.useState(false);
  const [showCompactAfterCollapse, setShowCompactAfterCollapse] = React.useState(false);

  // Load settings to get translations
  const { settings, loading: settingsLoading } = useSettingsRedux();

  const showCompactView = isProductConfirmationMode && selectedProduct;

  // Handle transition to compact view
  React.useEffect(() => {
    if (showCompactView && !showCompactAfterCollapse && !isTransitioningToCompact) {
      // Start transition by sliding out of view
      setIsTransitioningToCompact(true);
    }
  }, [showCompactView]);

  // Handle the second stage of the transition when isTransitioningToCompact becomes true
  React.useEffect(() => {
    if (isTransitioningToCompact) {
      // After slide-out animation completes, show compact view
      // Add a small buffer (50ms) to ensure the CSS animation fully completes
      const timer = setTimeout(() => {
        setShowCompactAfterCollapse(true);
        setIsTransitioningToCompact(false);
      }, 450); // 400ms CSS animation + 50ms buffer

      return () => clearTimeout(timer);
    }
  }, [isTransitioningToCompact]);

  // Handle reset when exiting compact mode
  React.useEffect(() => {
    if (!showCompactView && (showCompactAfterCollapse || isTransitioningToCompact)) {
      setShowCompactAfterCollapse(false);
      setIsTransitioningToCompact(false);
    }
  }, [showCompactView, showCompactAfterCollapse, isTransitioningToCompact]);

  // Get titlebar title from translations or use fallback
  const getTitlebarTitle = () => {
    if (settings?.translations?.bar?.start) {
      return settings.translations.bar.start;
    }
    // Fallback to the current hardcoded title
    return ``;
  };

  // Send expand/collapse messages to parent when appState changes
  React.useEffect(() => {
    if (appState === 'expanded') {
      parentFrameMessaging.sendExpandIframe();
    } else if (appState === 'collapsed') {
      parentFrameMessaging.sendCollapseIframe();
    }
  }, [appState]);

  const handleToggleExpand = () => {
    if (showCompactAfterCollapse || isTransitioningToCompact) return; // Don't allow expand/collapse during transition or in compact mode

    // Toggle between expanded and collapsed
    const newState: AppState = appState === 'expanded' ? 'collapsed' : 'expanded';
    setAppState(newState);
  };

  // Determine the layout class based on app state and transition phases
  const getLayoutClass = () => {
    return showCompactAfterCollapse
      ? 'compact-mode'
      : isTransitioningToCompact
      ? 'transitioning-to-compact'
      : appState === 'dismissed'
      ? 'dismissed'
      : ''; // Default expanded visual layout regardless of app state
  };

  return (
    <div className={`main-layout ${getLayoutClass()}`}>
      <div className="main-layout-shared-container">
        {!showCompactAfterCollapse && (
          <div className="title-bar-container" onClick={handleToggleExpand}>
            <TitleBar
              title={getTitlebarTitle()}
              timer={formatTime(timeRemaining)}
              appState={appState}
              setAppState={setAppState}
              location={location}
            />
          </div>
        )}
        <main className="main-layout-content">
          {showCompactAfterCollapse && selectedProduct ? (
            <SelectedProductCompactView product={selectedProduct} walletAmount={statusBarProps.walletAmount} />
          ) : (
            <div className="children-wrapper">{children}</div>
          )}
        </main>
        <StatusBar {...statusBarProps} rootPath={rootPath} />
      </div>
    </div>
  );
};

export default MainLayout;
